import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { get, post } from "@/api/client";

interface SignInRequest {
  client_name: string;
  redirect_host: string;
  you: { name: string; email: string };
  blocked: string;
}

/**
 * /connect/app?request=… — an AI app (Claude Desktop, claude.ai) asks to work
 * in the Console as you. Approving sends you back to the app, signed in; the app
 * can then do what you can, and every change it makes is logged under your name.
 */
export default function ConnectAppPage() {
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";
  const details = useQuery({
    queryKey: ["mcp-request", requestId],
    queryFn: () => get<SignInRequest>(`/mcp/oauth/requests/${encodeURIComponent(requestId)}`),
    enabled: !!requestId && window.top === window.self,
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (allow: boolean) => post<{ redirect: string }>(
      `/mcp/oauth/requests/${encodeURIComponent(requestId)}/${allow ? "approve" : "deny"}`),
    onSuccess: (result) => window.location.assign(result.redirect),
  });

  // Inside someone else's frame the Allow button could be clicked for you.
  const framed = window.top !== window.self;

  const body = () => {
    if (framed) return <Alert severity="error">Open this page in its own browser tab.</Alert>;
    if (!requestId) return <Alert severity="error">This link is missing its sign-in request.</Alert>;
    if (details.isLoading) return <CircularProgress size={24} />;
    if (details.error || !details.data) {
      return <Alert severity="error">
        {(details.error as Error | null)?.message ?? "This sign-in request has expired."} Start
        again from the app.
      </Alert>;
    }
    const d = details.data;
    return (
      <Stack spacing={2}>
        <Typography variant="body1">
          <b>{d.client_name}</b> wants to work in the MultiDoc Console as you
          ({d.you.email}).
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
          <li><Typography variant="body2">It can look up and change what you can: deals,
            pursuits, proposals, partners, licences, demos, files.</Typography></li>
          <li><Typography variant="body2">It can upload files to deals and publish them to a
            customer's portal, which the customer sees.</Typography></li>
          <li><Typography variant="body2">It cannot delete, revoke, or touch keys and settings.
            Every change is logged under your name, with an Undo where there is one.</Typography></li>
          <li><Typography variant="body2">You can disconnect it at any time under Your account →
            Connected AI apps.</Typography></li>
        </Box>
        <Typography variant="caption" color="text.secondary">
          You will be sent back to {d.redirect_host}.
        </Typography>
        {d.blocked ? <Alert severity="warning">{d.blocked}</Alert> : null}
        {decide.error ? <Alert severity="error">{(decide.error as Error).message}</Alert> : null}
        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" disabled={!!d.blocked || decide.isPending}
                  onClick={() => decide.mutate(true)}>
            Allow
          </Button>
          <Button disabled={decide.isPending} onClick={() => decide.mutate(false)}>Deny</Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center", p: 2 }}>
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 520, width: "100%" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Connect an AI app</Typography>
        {body()}
      </Paper>
    </Box>
  );
}
